import { BrandLogo } from '@seek/branding-assets';
import {
  type CompanyProfile,
  type CurrentPageSectionType,
  createImageObject,
} from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Column,
  Columns,
  Heading,
  Hidden,
  Tab,
  Tabs,
  TabsProvider,
  TextLink,
} from 'braid-design-system';
import { useEffect, useState } from 'react';
import { CompanyProfilesPage } from '../../../paths.ts';
import { HIDE_REVIEWS_COMPANY_IDS } from '../../../shared/constants/redirection';
import translations from './.vocab/index.ts';
import * as styles from './CompanyProfileLayout.css.ts';
import { WriteReviewButton } from './WriteReview.tsx';

interface CompanyProfileLayoutStickyHeaderProps {
  companyName: string;
  companyProfileModel: CompanyProfile;
  onActiveTabChanged: (tab: string | undefined) => void;
  origin: CurrentPageSectionType;
  tabRef: React.RefObject<HTMLDivElement>;
  aboutPageLink?: string;
  activeTab?: string;
  branding?: {
    coverImageUrl?: string;
    logoImageUrl?: string;
  };
  shouldShowSalariesPage?: boolean;
}

export function CompanyProfileLayoutStickyHeader({
  companyName,
  branding,
  activeTab,
  onActiveTabChanged,
  shouldShowSalariesPage,
  aboutPageLink,
  origin,
  companyProfileModel,
  tabRef,
}: CompanyProfileLayoutStickyHeaderProps) {
  const logoImageAsset =
    branding?.logoImageUrl &&
    createImageObject('jdpLogo', branding.logoImageUrl);
  const { t } = useTranslations(translations);
  const [showStickyHeader, setShowStickyHeader] = useState(false);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (tabRef.current) {
            const currentScrollY = window.scrollY;
            const tabsRect = tabRef.current.getBoundingClientRect();
            const scrollingDown = currentScrollY > lastScrollY;

            setShowStickyHeader((prevShow) => {
              // When scrolling down: show sticky when tabs are 5px past the top
              if (scrollingDown && !prevShow && tabsRect.bottom <= -1) {
                return true;
              }

              // When scrolling up: hide sticky when tabs are 5px back into view
              if (!scrollingDown && prevShow && tabsRect.bottom >= 1) {
                return false;
              }

              // No change in other cases
              return prevShow;
            });

            lastScrollY = currentScrollY;
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      className={
        showStickyHeader ? styles.stickyHeaderShow : styles.stickyHeaderHidden
      }
    >
      <Box className={styles.stickyHeaderContent} background="body">
        <Box background="body">
          <Columns space="small" align="left" alignY="center">
            <Column width="content">
              {logoImageAsset && (
                <Box className={styles.stickyLogo}>
                  <BrandLogo logo={logoImageAsset} />
                </Box>
              )}
            </Column>
            <Column>
              <Box marginTop="medium">
                {aboutPageLink ? (
                  <TextLink
                    href={aboutPageLink}
                    onClick={(e) => {
                      e.preventDefault();
                      onActiveTabChanged(CompanyProfilesPage.About);
                    }}
                  >
                    <Heading level="4" component="span">
                      {companyName}
                    </Heading>
                  </TextLink>
                ) : (
                  <Heading level="4" component="span">
                    {companyName}
                  </Heading>
                )}
              </Box>
            </Column>
          </Columns>
        </Box>

        {/* Tabs Section */}
        <Box paddingTop="small" background="body">
          <Box position="relative" width="full">
            <Box className={styles.stickyReviewButton}>
              <Hidden below="tablet">
                <WriteReviewButton
                  companyProfileName={companyProfileModel.name}
                  origin={origin}
                />
              </Hidden>
            </Box>
          </Box>
          <TabsProvider
            id="company-profile-sticky"
            selectedItem={activeTab}
            onChange={(_index, item) => onActiveTabChanged(item)}
          >
            <Tabs label="" divider="full" reserveHitArea={true}>
              <Tab item={CompanyProfilesPage.About}>{t('About')}</Tab>
              <Tab item={CompanyProfilesPage.Culture}>
                {t('Life and Culture')}
              </Tab>
              <Tab item={CompanyProfilesPage.Jobs}>{t('Jobs')}</Tab>
              {shouldShowSalariesPage && (
                <Tab
                  item={CompanyProfilesPage.Salaries}
                  badge={<Badge tone="positive">{t('New')}</Badge>}
                >
                  {t('Salaries')}
                </Tab>
              )}
              {!HIDE_REVIEWS_COMPANY_IDS.includes(companyProfileModel.id) && (
                <Tab item={CompanyProfilesPage.Reviews}>{t('Reviews')}</Tab>
              )}
            </Tabs>
          </TabsProvider>
        </Box>
      </Box>
    </Box>
  );
}
